import { useIsApiReady } from "@/hooks/use-api-ready";
import {
  customPromptControllerCreate,
  customPromptControllerDelete,
  customPromptControllerGetAll,
  customPromptControllerPatch,
} from "@/lib/gen-api";
import type {
  PatchCustomPromptBodyDto,
  PostCustomPromptBodyDto,
} from "@/lib/gen-api/types.gen";
import { queryKeys } from "@/lib/query-keys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const useCustomPromptsData = (groupUuid: string | null | undefined) => {
  const queryClient = useQueryClient();
  const isApiReady = useIsApiReady();

  const { data, isPending, error } = useQuery({
    enabled: isApiReady && !!groupUuid,
    queryKey: queryKeys.customPrompts({ groupUuid: groupUuid! }),
    queryFn: () =>
      customPromptControllerGetAll({
        query: {
          filter: {
            groupUuid: groupUuid!,
          },
        },
      }).then((res) => res.data?.data ?? []),
  });

  const { mutateAsync: createCustomPrompt, isPending: isCreating } =
    useMutation({
      mutationFn: async (body: Omit<PostCustomPromptBodyDto, "groupUuid">) => {
        if (!groupUuid) {
          throw new Error("Group UUID is required");
        }
        return customPromptControllerCreate({
          body: {
            groupUuid,
            ...body,
          },
        }).then((res) => res.data);
      },
      onSettled: () => {
        if (!groupUuid) return;
        queryClient.invalidateQueries({
          queryKey: queryKeys.customPrompts({ groupUuid }),
        });
      },
    });

  const { mutateAsync: updateCustomPrompt, isPending: isUpdating } =
    useMutation({
      mutationFn: async (params: {
        customPromptUuid: string;
        body: PatchCustomPromptBodyDto;
      }) => {
        return customPromptControllerPatch({
          path: {
            customPromptUuid: params.customPromptUuid,
          },
          body: params.body,
        }).then((res) => res.data);
      },
      onSettled: () => {
        if (!groupUuid) return;
        queryClient.invalidateQueries({
          queryKey: queryKeys.customPrompts({ groupUuid }),
        });
      },
    });

  const { mutateAsync: deleteCustomPrompt, isPending: isDeleting } =
    useMutation({
      mutationFn: async (customPromptUuid: string) => {
        await customPromptControllerDelete({
          path: { customPromptUuid },
        });
      },
      onSettled: () => {
        if (!groupUuid) return;
        queryClient.invalidateQueries({
          queryKey: queryKeys.customPrompts({ groupUuid }),
        });
      },
    });

  return {
    customPrompts: data ?? [],
    isLoading: isPending,
    error,
    createCustomPrompt,
    isCreating,
    updateCustomPrompt,
    isUpdating,
    deleteCustomPrompt,
    isDeleting,
  };
};

export const useOrganizationCustomPromptsData = (
  organizationUuid: string | null | undefined,
) => {
  const queryClient = useQueryClient();
  const isApiReady = useIsApiReady();

  const { data, isPending, error } = useQuery({
    enabled: isApiReady && !!organizationUuid,
    queryKey: queryKeys.customPrompts({
      organizationUuid: organizationUuid!,
    }),
    queryFn: () =>
      customPromptControllerGetAll({
        query: {
          filter: {
            organizationUuid: organizationUuid!,
          },
        },
      }).then((res) => res.data?.data ?? []),
  });

  const invalidate = () => {
    if (!organizationUuid) return;
    queryClient.invalidateQueries({
      queryKey: queryKeys.customPrompts({ organizationUuid }),
    });
  };

  const { mutateAsync: createCustomPrompt, isPending: isCreating } =
    useMutation({
      mutationFn: async (body: { base: string; name: string }) => {
        if (!organizationUuid) {
          throw new Error("Organization UUID is required");
        }
        const response = await customPromptControllerCreate({
          body: {
            organizationUuid,
            base: body.base,
            name: body.name,
          },
        });
        if (!response.response.ok || !response.data) {
          throw new Error("Failed to create custom prompt");
        }
        return response.data;
      },
      onSettled: invalidate,
    });

  const { mutateAsync: updateCustomPrompt, isPending: isUpdating } =
    useMutation({
      mutationFn: async (params: {
        customPromptUuid: string;
        body: PatchCustomPromptBodyDto;
      }) => {
        const response = await customPromptControllerPatch({
          path: {
            customPromptUuid: params.customPromptUuid,
          },
          body: params.body,
        });
        if (!response.response.ok || !response.data) {
          throw new Error("Failed to update custom prompt");
        }
        return response.data;
      },
      onSettled: invalidate,
    });

  const { mutateAsync: deleteCustomPrompt, isPending: isDeleting } =
    useMutation({
      mutationFn: async (customPromptUuid: string) => {
        const response = await customPromptControllerDelete({
          path: { customPromptUuid },
        });
        if (!response.response.ok) {
          throw new Error("Failed to delete custom prompt");
        }
      },
      onSettled: invalidate,
    });

  return {
    customPrompts: data ?? [],
    isLoading: isPending,
    error,
    createCustomPrompt,
    isCreating,
    updateCustomPrompt,
    isUpdating,
    deleteCustomPrompt,
    isDeleting,
  };
};
